<template>
  <div class="stock-slips opening-balance-cost-page">
    <KTCodePreview title="">
      <template #title>
        <div class="obc-page-heading">
          <h3 class="card-label font-weight-bold mb-2">Nhập giá vốn đầu kỳ</h3>
          <div class="d-flex flex-wrap align-items-center mb-2">
            <b-badge variant="light" class="obc-badge-draft border text-secondary mr-2">Bản nháp</b-badge>
            <b-badge variant="primary" class="obc-badge-mode font-weight-normal">
              Chế độ: {{ isImeiFlow ? 'IMEI' : 'SP thường' }}
            </b-badge>
          </div>
          <p class="text-muted small mb-0 obc-page-subtitle">
            Gán giá vốn ban đầu cho sản phẩm/IMEI — không thay đổi số lượng tồn.
          </p>
        </div>
      </template>

      <template #toolbar>
        <div class="d-flex flex-wrap align-items-center justify-content-end obc-toolbar-actions">
          <b-button variant="link" size="sm" class="text-muted obc-toolbar-btn px-2" :disabled="busy" @click="goCancel">
            <i class="fas fa-times mr-1" aria-hidden="true"></i>{{ canEdit ? 'Hủy' : 'Quay lại' }}
          </b-button>
          <b-button
            v-if="canEdit"
            variant="outline-secondary"
            size="sm"
            class="obc-toolbar-btn"
            :disabled="busy"
            @click="saveLocalDraft"
          >
            Lưu nháp
          </b-button>
          <b-button
            v-if="canEdit"
            variant="success"
            size="sm"
            class="obc-toolbar-btn obc-btn-apply font-weight-bold"
            :disabled="!canSubmitFoot"
            @click="submitBatches"
          >
            <i class="fas fa-check mr-1" aria-hidden="true"></i>Lưu &amp; áp dụng
          </b-button>
          <b-dropdown v-if="canEdit" variant="link" toggle-class="obc-toolbar-more px-2" no-caret right>
            <template #button-content>
              <span class="text-muted"><i class="fas fa-ellipsis-h" aria-hidden="true"></i></span>
            </template>
            <b-dropdown-item @click="downloadExcelTemplate">
              <i class="fas fa-file-download mr-2 text-muted" aria-hidden="true"></i>Tải mẫu Excel
            </b-dropdown-item>
            <b-dropdown-item @click="restoreLocalDraft">
              <i class="fas fa-undo mr-2 text-muted" aria-hidden="true"></i>Khôi phục nháp cục bộ
            </b-dropdown-item>
            <b-dropdown-item @click="clearLocalDraft">
              <i class="fas fa-trash-alt mr-2 text-muted" aria-hidden="true"></i>Xóa nháp cục bộ
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

      <template #preview>
        <b-alert v-if="!canViewOrEdit" show variant="light" class="border">
          Cần quyền <code>OPENING_BALANCE_COST_VIEW</code> hoặc <code>OPENING_BALANCE_COST_EDIT</code>.
        </b-alert>

        <template v-else>
          <div class="obc-form-layout">
            <b-alert v-if="!hasHeaderCompanyId" show variant="warning" class="small mb-4">
              Chưa có doanh nghiệp trong phiên làm việc — chọn <strong>doanh nghiệp trên header</strong> rồi tải lại trang.
            </b-alert>

            <b-row class="m-0">
              <b-col cols="12" lg="4" class="pl-lg-0 pr-lg-3">
                <b-card class="obc-side-card border mb-4" body-class="py-3 px-3">
                  <h5 class="font-weight-bold text-dark mb-3">Thông tin phiếu</h5>

                  <div class="mb-3">
                    <label class="d-block small font-weight-bold text-dark mb-2">Loại nhập</label>
                    <div class="obc-segment-track d-flex p-1 rounded-lg bg-white border">
                      <button
                        type="button"
                        class="obc-segment-btn flex-fill font-weight-bold"
                        :class="{ active: !isImeiFlow }"
                        :disabled="busy || !canEdit"
                        @click.prevent="switchToProductFlow"
                      >
                        SP thường
                      </button>
                      <button
                        type="button"
                        class="obc-segment-btn flex-fill font-weight-bold"
                        :class="{ active: isImeiFlow }"
                        :disabled="busy || !canEdit"
                        @click.prevent="switchToImeiFlow"
                      >
                        Theo IMEI
                      </button>
                    </div>
                    <p class="text-muted small mb-0 mt-2">
                      <template v-if="!isImeiFlow">
                        Gán giá vốn theo Mã hàng (ProductStock). Không yêu cầu IMEI.
                      </template>
                      <template v-else>
                        Gán giá vốn theo IMEI (ProductImeiStock). File Excel cần <strong>IMEI serial</strong>.
                      </template>
                    </p>
                  </div>

                  <StockSlipOpening
                    stack-warehouse-under-date
                    :hide-warehouse="!isImeiFlow"
                    :stock-slip="stockMeta"
                    :stores="storesForAutosuggest"
                    :editable="canEdit && hasHeaderCompanyId"
                    @onSelectedSourceWarehouse="onSelectedSourceWarehouse"
                    @onInputChangeSourceWarehouse="onInputChangeSourceWarehouse"
                  />

                  <b-form-group class="mb-3" label-class="font-weight-bold small">
                    <template #label>Tiền tệ</template>
                    <b-form-select
                      v-model="currencyCode"
                      class="select-style"
                      size="sm"
                      :options="currencyOptions"
                      :disabled="true"
                    />
                  </b-form-group>

                  <b-alert show variant="light" class="obc-hints-alert border-info mb-0">
                    <div class="d-flex">
                      <i class="fas fa-info-circle text-primary mt-1 mr-2" aria-hidden="true"></i>
                      <div class="small text-dark">
                        <strong class="d-block mb-1">Lưu ý</strong>
                        <ul class="mb-0 pl-3">
                          <li>Màn hình này chỉ gán giá vốn, không nhập/hiển thị số lượng tồn.</li>
                          <li v-if="!isImeiFlow">SP thường: cần productCode + giá vốn.</li>
                          <li v-else>IMEI: cần productCode + imei + giá vốn.</li>
                        </ul>
                      </div>
                    </div>
                  </b-alert>
                </b-card>
              </b-col>

              <b-col cols="12" lg="8" class="pr-lg-0 pl-lg-3">
                <b-alert v-if="!canEdit" show variant="info" class="small py-2 mb-4">Chế độ xem — không chỉnh sửa được.</b-alert>

                <b-alert
                  v-if="submitErrorBannerShow"
                  id="obc-submit-errors-banner"
                  ref="obcSubmitErrBanner"
                  :variant="submitErrorBannerVariant"
                  show
                  class="small mb-4 obc-submit-error-banner"
                >
                  <div class="font-weight-bold mb-1">Lưu batch — có lỗi cần kiểm tra</div>
                  <p v-if="submitLastGlobalError" class="mb-2 text-break">{{ submitLastGlobalError }}</p>
                  <p v-if="submitFailedLineCount" class="mb-2">
                    Có {{ submitFailedLineCount }} dòng không áp được — xem cột
                    <strong>Phản hồi lưu</strong> trong bảng chi tiết và bảng tổng hợp phía dưới (nếu có).
                  </p>
                  <b-button
                    v-if="resultBatches.length"
                    variant="outline-secondary"
                    size="sm"
                    class="mr-2"
                    @click.prevent="scrollToBatchDetailSection"
                  >
                    Xem bảng kết quả chi tiết
                  </b-button>
                  <b-button v-if="submitFailedLineCount" variant="outline-primary" size="sm" @click.prevent="scrollToLinesTableSection">
                    Về danh sách dòng chi tiết
                  </b-button>
                </b-alert>

                <b-card
                  v-if="canView"
                  id="obc-lines-table-section"
                  class="mb-4 obc-lines-card border"
                  header-tag="div"
                  header-bg-variant="white"
                  header-class="py-3 px-3 border-bottom"
                  body-class="p-0"
                >
                  <template #header>
                    <div class="d-flex flex-wrap align-items-start justify-content-between w-100">
                      <div class="mb-2 mb-md-0 pr-md-3">
                        <span class="font-weight-bold text-dark d-block">Chi tiết dòng</span>
                        <span class="small text-muted">{{ linesDetailSubtitle }}</span>
                      </div>
                      <div v-if="canEdit" class="d-flex flex-wrap align-items-center justify-content-end obc-detail-toolbar">
                        <b-button variant="outline-secondary" size="sm" class="mr-2 mb-1" @click="downloadExcelTemplate">
                          <i class="fas fa-download mr-1" aria-hidden="true"></i>Tải mẫu Excel
                        </b-button>
                        <b-button variant="outline-secondary" size="sm" class="mr-2 mb-1" :disabled="!scopeOk || busy" @click="showModalImport">
                          <i class="fas fa-file-upload mr-1" aria-hidden="true"></i>Nhập từ Excel
                        </b-button>
                        <b-button variant="outline-primary" size="sm" class="mb-1" :disabled="!scopeOk || busy" @click="addEmptyLine">
                          <i class="fas fa-plus mr-1" aria-hidden="true"></i>Thêm dòng
                        </b-button>
                      </div>
                    </div>
                  </template>

                  <template v-if="!isImeiFlow">
                    <b-table
                      bordered
                      hover
                      show-empty
                      empty-text="Chưa có dòng — nhập từ Excel."
                      class="obc-lines-table mb-0"
                      thead-class="text-nowrap obc-table-head"
                      tbody-class="align-middle"
                      :items="lineTableItemsProduct"
                      :fields="lineFieldsProduct"
                      :tbody-tr-class="tbodyTrClassSubmitErrors"
                    >
                      <template #cell(productCell)="{ item }">
                        <ProductCommonInfo class="obc-product-cell" :product="lineProductForInfo(item._idx, false)" />
                      </template>
                      <template #cell(originalPrice)="{ item }">
                        <BInputNumber
                          v-if="canEdit"
                          :key="'op-p-' + item.lineKey"
                          :value="linesProduct[item._idx] ? linesProduct[item._idx].originalPrice : null"
                          placeholder="Giá vốn"
                          size="sm"
                          class="obc-price-input"
                          :input-class="'w-100 text-right'"
                          :disabled="!canEdit"
                          allow-decimal
                          @valueChange="setLineOriginalPrice(item._idx, $event)"
                        />
                        <span v-else class="text-right d-block">{{ formatLineOriginalPrice(linesProduct[item._idx]) }}</span>
                      </template>
                      <template #cell(batchFeedback)="{ item }">
                        <template v-if="submitLineErrors[item.lineKey]">
                          <b-badge variant="danger" pill class="font-weight-normal text-break badge-wrap">
                            {{ submitLineErrors[item.lineKey] }}
                          </b-badge>
                        </template>
                        <b-badge v-else-if="lineResolveOkBadge(item)" variant="success" pill class="font-weight-normal">
                          {{ lineResolveOkBadge(item) }}
                        </b-badge>
                        <span v-else class="text-muted">—</span>
                      </template>
                      <template #cell(actions)="{ item }">
                        <b-button v-if="canEdit" variant="link" class="p-0 text-danger" size="sm" @click="removeRow(item._idx)">
                          <i class="fas fa-trash-alt" aria-hidden="true"></i>
                        </b-button>
                      </template>
                    </b-table>
                  </template>
                  <template v-else>
                    <b-table
                      bordered
                      hover
                      show-empty
                      empty-text="Chưa có dòng — nhập từ Excel (Mã hàng + IMEI + Giá vốn)."
                      class="obc-lines-table mb-0"
                      thead-class="text-nowrap obc-table-head"
                      tbody-class="align-middle"
                      :items="lineTableItemsImei"
                      :fields="lineFieldsImei"
                      :tbody-tr-class="tbodyTrClassSubmitErrors"
                    >
                      <template #cell(productCell)="{ item }">
                        <ProductCommonInfo class="obc-product-cell" :product="lineProductForInfo(item._idx, true)" />
                      </template>
                      <template #cell(imeiCell)="{ item }">
                        <span class="text-break d-block small font-weight-bold">{{ linesImei[item._idx] ? linesImei[item._idx].imeiNo || '—' : '—' }}</span>
                      </template>
                      <template #cell(originalPrice)="{ item }">
                        <BInputNumber
                          v-if="canEdit"
                          :key="'op-i-' + item.lineKey"
                          :value="linesImei[item._idx] ? linesImei[item._idx].originalPrice : null"
                          placeholder="Giá vốn"
                          size="sm"
                          class="obc-price-input"
                          :input-class="'w-100 text-right'"
                          :disabled="!canEdit"
                          allow-decimal
                          @valueChange="setLineOriginalPrice(item._idx, $event)"
                        />
                        <span v-else class="text-right d-block">{{ formatLineOriginalPrice(linesImei[item._idx]) }}</span>
                      </template>
                      <template #cell(batchFeedback)="{ item }">
                        <template v-if="submitLineErrors[item.lineKey]">
                          <b-badge variant="danger" pill class="font-weight-normal text-break badge-wrap">
                            {{ submitLineErrors[item.lineKey] }}
                          </b-badge>
                        </template>
                        <b-badge v-else-if="lineResolveOkBadge(item)" variant="success" pill class="font-weight-normal">
                          {{ lineResolveOkBadge(item) }}
                        </b-badge>
                        <span v-else class="text-muted">—</span>
                      </template>
                      <template #cell(actions)="{ item }">
                        <b-button v-if="canEdit" variant="link" class="p-0 text-danger" size="sm" @click="removeRow(item._idx)">
                          <i class="fas fa-trash-alt" aria-hidden="true"></i>
                        </b-button>
                      </template>
                    </b-table>
                  </template>

                  <div
                    v-if="linesCount > 0"
                    class="obc-lines-pagination border-top px-3 py-2 d-flex flex-wrap align-items-center justify-content-between"
                  >
                    <b-form-select
                      v-model.number="linesPagination.pageSize"
                      class="select-style obc-page-size-select mb-1 mb-md-0"
                      size="sm"
                      :options="linePageSizeOptions"
                      @change="onLinesPageSizeChange"
                    />
                    <b-pagination
                      v-if="linesTotalPages > 1"
                      v-model="linesPagination.page"
                      class="mb-0"
                      align="center"
                      size="sm"
                      :total-rows="linesCount"
                      :per-page="linesPageSizeClamped"
                    />
                  </div>

                  <div
                    v-if="linesCount > 0"
                    class="obc-lines-total border-top bg-light px-3 py-2 d-flex flex-wrap justify-content-between align-items-center small"
                  >
                    <span class="font-weight-bold text-dark">Tổng {{ linesCount }} dòng</span>
                    <span class="font-weight-bold">{{ totalOriginalPriceFormatted }}</span>
                  </div>
                </b-card>

                <b-card
                  v-if="canView && resultBatches.length"
                  ref="obcBatchResultCard"
                  id="obc-batch-result-detail"
                  class="mt-2 mb-4 border obc-batch-result-card"
                  header-bg-variant="light"
                  header-class="py-2 px-3"
                >
                  <template #header>
                    <span class="font-weight-bold">Kết quả gửi gần nhất</span>
                  </template>
                  <div v-for="(b, i) in resultBatches" :key="'b' + i" class="mb-3">
                    <p class="small mb-1 font-weight-bold">
                      Batch {{ i + 1 }} — đã áp {{ b.processedCount }} · lỗi {{ b.failedCount }}
                    </p>
                    <b-table small striped bordered :items="b.results || []" :fields="resultFields" class="mb-0">
                      <template #cell(status)="d">
                        <b-badge :variant="batchResultStatusBadgeVariant(d.item.status)">{{ d.item.status }}</b-badge>
                      </template>
                      <template #cell(storesUpdatedCount)="d">
                        {{ formatStoresUpdatedCell(d.item.storesUpdatedCount) }}
                      </template>
                    </b-table>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </div>
        </template>
      </template>
    </KTCodePreview>

    <div v-if="canViewOrEdit && hasHeaderCompanyId" class="obc-sticky-actions border-top shadow-sm bg-white">
      <div class="obc-sticky-inner d-flex flex-wrap align-items-center justify-content-between py-3 px-3 px-lg-4">
        <div class="small font-weight-bold text-dark mb-2 mb-md-0">{{ stickySummaryText }}</div>
        <div class="d-flex flex-wrap align-items-center justify-content-md-end obc-toolbar-actions">
          <template v-if="canEdit">
            <b-button variant="link" size="sm" class="text-muted obc-toolbar-btn px-2" :disabled="busy" @click="goCancel">
              <i class="fas fa-times mr-1" aria-hidden="true"></i>Hủy
            </b-button>
            <b-button variant="outline-secondary" size="sm" class="obc-toolbar-btn" :disabled="busy" @click="saveLocalDraft">
              Lưu nháp
            </b-button>
            <b-button variant="success" size="sm" class="obc-toolbar-btn obc-btn-apply font-weight-bold" :disabled="!canSubmitFoot" @click="submitBatches">
              <i class="fas fa-check mr-1" aria-hidden="true"></i>Lưu &amp; áp dụng
            </b-button>
          </template>
          <b-button v-else variant="secondary" size="sm" class="font-weight-bold" @click="goCancel">Quay lại</b-button>
        </div>
      </div>
    </div>

    <b-modal
      ref="import-modal"
      hide-footer
      hide-header
      size="lg"
      content-class="obc-import-modal"
      @hidden="resetExcelImportState"
    >
      <div class="obc-import-modal__head d-flex align-items-start justify-content-between mb-1">
        <div class="d-flex align-items-start">
          <span class="obc-import-modal__icon mr-3">
            <i class="fas fa-file-excel" aria-hidden="true"></i>
          </span>
          <div>
            <h5 class="font-weight-bold text-dark mb-1">Nhập từ Excel</h5>
            <p class="text-muted small mb-0">{{ excelImportModeSubtitle }}</p>
          </div>
        </div>
        <button type="button" class="close obc-import-modal__close" aria-label="Đóng" @click="closeModalImport">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="obc-import-modal__section mt-4">
        <div class="font-weight-bold small text-dark mb-2">Cột yêu cầu</div>
        <div class="d-flex flex-wrap obc-import-cols mb-2">
          <span
            v-for="col in excelImportColumns"
            :key="col.key"
            class="obc-import-col-badge mr-2 mb-2"
          >
            <span class="obc-import-col-dot" :class="col.required ? 'obc-import-col-dot--required' : 'obc-import-col-dot--optional'"></span>
            {{ col.key }}
          </span>
        </div>
        <b-button variant="link" class="p-0 obc-import-template-link" @click="downloadExcelTemplate">
          Tải file mẫu (.xlsx)
        </b-button>
      </div>

      <div
        class="obc-import-dropzone mt-4"
        :class="{ 'obc-import-dropzone--active': excelImportDragOver }"
        @dragover.prevent="onExcelDragOver"
        @dragleave.prevent="onExcelDragLeave"
        @drop.prevent="onExcelDrop"
      >
        <input
          ref="excelFileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="d-none"
          @change="onExcelFileSelected"
        />
        <div v-if="excelImportFileName" class="obc-import-file-info">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center min-width-0">
              <i class="fas fa-file-excel text-success mr-2" aria-hidden="true"></i>
              <div class="min-width-0">
                <div class="font-weight-bold text-truncate">{{ excelImportFileName }}</div>
                <div v-if="excelStaging.length" class="small text-muted">Đọc được {{ excelStaging.length }} dòng</div>
              </div>
            </div>
            <b-button variant="link" class="text-danger p-0 ml-2 flex-shrink-0" @click="clearExcelFile">
              <i class="fas fa-times" aria-hidden="true"></i>
            </b-button>
          </div>
        </div>
        <div v-else class="text-center py-2">
          <i class="fas fa-cloud-upload-alt obc-import-dropzone__icon text-muted mb-3 d-block" aria-hidden="true"></i>
          <p class="mb-2 text-dark">Kéo &amp; thả file Excel vào đây hoặc</p>
          <b-button variant="outline-primary" size="sm" @click="triggerExcelFilePick">
            chọn từ máy
          </b-button>
          <p class="text-muted small mb-0 mt-3">Hỗ trợ .xlsx, .xls, .csv · Tối đa 10MB</p>
        </div>
      </div>

      <div class="obc-import-api-note small mt-4">
        <i class="fas fa-info-circle text-warning mr-2" aria-hidden="true"></i>
        <span v-html="excelImportApiNote"></span>
      </div>

      <div class="obc-import-modal__footer d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        <b-button variant="link" class="text-muted px-0" @click="closeModalImport">Đóng</b-button>
        <b-button
          variant="primary"
          class="font-weight-bold px-4"
          :disabled="busyImport || !canApplyExcelImport"
          @click="applyExcelImport"
        >
          <b-spinner v-if="busyImport" small class="mr-1" />
          <i v-else class="fas fa-file-upload mr-1" aria-hidden="true"></i>
          Ghép vào chi tiết
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import StockSlipOpening from '@/view/components/stock-slips/StockSlipOpening.vue';
import ProductCommonInfo from '@/view/components/products/ProductCommonInfo.vue';
import xlsx from 'xlsx';
import permissionMixin from '@/view/mixins/permission.mixin';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import { getData } from '@/utils/saveDataToLocal';
import moment from 'moment';
import {
  makeToastFaile,
  makeToastSuccess,
  convertPrice,
  parseDecimalNumber,
} from '@/utils/common';
import { getToastInstance } from '@/utils/toastHelper';
import { postOpeningStockSlipValidateImportProduct } from '@/services/openingStockSlipImport.service';
import {
  postOpeningBalanceCostBatchProductStock,
  postOpeningBalanceCostBatchImeiStock,
  postOpeningBalanceCostValidateImportImei,
} from '@/services/openingBalanceCost.service';

const K_PRODUCT = 'product';
const K_IMEI = 'imei';

export default {
  name: 'FormOpeningBalanceCost',
  components: {
    KTCodePreview,
    StockSlipOpening,
    ProductCommonInfo,
    BInputNumber,
  },
  mixins: [permissionMixin],
  data() {
    return {
      busy: false,
      busyImport: false,
      allStoresFlat: [],
      form: {
        companyId: null,
        storeId: null,
      },
      stockMeta: {
        stockSlipDate: moment().format('DD/MM/YYYY'),
        validateState: true,
        notes: '',
        sourceWarehouseId: null,
        sourceWarehouseName: '',
      },
      linesProduct: [],
      linesImei: [],
      chunkSize: 200,
      resultBatches: [],
      submitLineErrors: {},
      submitLastGlobalError: '',
      excelStaging: [],
      excelImportFileName: '',
      excelImportDragOver: false,
      currencyCode: 'VND',
      currencyOptions: [{ value: 'VND', text: 'VND — Việt Nam Đồng' }],
      linesPagination: {
        page: 1,
        pageSize: 50,
      },
      K_IMEI,
      K_PRODUCT,
      linePageSizeOptions: [
        { value: 25, text: '25 / trang' },
        { value: 50, text: '50 / trang' },
        { value: 100, text: '100 / trang' },
      ],
      resultFields: [
        { key: 'lineKey', label: 'lineKey' },
        { key: 'productId', label: 'productId' },
        { key: 'imeiId', label: 'imeiId' },
        { key: 'status', label: 'TT' },
        { key: 'oldOriginalPrice', label: 'Giá cũ', tdClass: 'text-right' },
        { key: 'newOriginalPrice', label: 'Giá mới', tdClass: 'text-right' },
        {
          key: 'storesUpdatedCount',
          label: 'SL kho cập nhật',
          tdClass: 'text-right text-nowrap',
        },
        { key: 'errorMessage', label: 'Lỗi' },
      ],
      lineFieldsProduct: [
        { key: 'count', label: 'STT', thStyle: { width: '4%' }, tdClass: 'text-center align-middle', thClass: 'text-center' },
        { key: 'productCell', label: 'Sản phẩm', thStyle: { minWidth: '14rem' } },
        {
          key: 'originalPrice',
          label: 'Đơn giá (giá vốn)',
          thStyle: { width: '12%' },
        },
        {
          key: 'batchFeedback',
          label: 'Phản hồi lưu',
          thStyle: { minWidth: '10rem', width: '22%' },
          tdClass: 'small align-middle text-break',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '3%' },
          tdClass: 'text-center align-middle',
        },
      ],
      lineFieldsImei: [
        { key: 'count', label: 'STT', thStyle: { width: '4%' }, tdClass: 'text-center align-middle', thClass: 'text-center' },
        { key: 'productCell', label: 'Sản phẩm', thStyle: { minWidth: '14rem' } },
        {
          key: 'imeiCell',
          label: 'IMEI',
          thStyle: { width: '11rem' },
        },
        {
          key: 'originalPrice',
          label: 'Đơn giá (giá vốn)',
          thStyle: { width: '12%' },
        },
        {
          key: 'batchFeedback',
          label: 'Phản hồi lưu',
          thStyle: { minWidth: '10rem', width: '22%' },
          tdClass: 'small align-middle text-break',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '3%' },
          tdClass: 'text-center align-middle',
        },
      ],
    };
  },
  computed: {
    canView() {
      return this.checkPermission('OPENING_BALANCE_COST_VIEW');
    },
    canEdit() {
      return this.checkPermission('OPENING_BALANCE_COST_EDIT');
    },
    canViewOrEdit() {
      return this.checkPermission([
        'OPENING_BALANCE_COST_VIEW',
        'OPENING_BALANCE_COST_EDIT',
      ]);
    },
    hasHeaderCompanyId() {
      return Number(this.form.companyId) > 0;
    },
    scopeOk() {
      if (!this.hasHeaderCompanyId) return false;
      if (this.isImeiFlow) return Number(this.form.storeId) > 0;
      return true;
    },
    storesForAutosuggest() {
      return this.storeOptions.map((s) => ({
        id: s.id,
        name: s.label,
        shortName: '',
      }));
    },
    storeOptions() {
      return this.allStoresFlat || [];
    },
    memoForApi() {
      return (
        String(this.stockMeta.notes || '').trim() ||
        (this.stockMeta.stockSlipDate
          ? `Ngày tham chiếu ${this.stockMeta.stockSlipDate}`
          : '')
      );
    },
    linesPageSizeClamped() {
      const n = Number(this.linesPagination.pageSize);
      if (!Number.isFinite(n) || n < 1) return 50;
      return Math.min(200, Math.max(1, n));
    },
    linesTotalPages() {
      const p = this.linesPageSizeClamped;
      return Math.max(1, Math.ceil((Number(this.linesCount) || 0) / p));
    },
    linesSliceStart() {
      const page = Math.min(Math.max(1, this.linesPagination.page), this.linesTotalPages);
      return (page - 1) * this.linesPageSizeClamped;
    },
    isImeiFlow() {
      return this.$route.name === 'add-opening-imei-balance-cost';
    },
    excelImportModeSubtitle() {
      return this.isImeiFlow ? 'Chế độ IMEI (ProductImeiStock)' : 'Chế độ SP thường (ProductStock)';
    },
    excelImportColumns() {
      if (this.isImeiFlow) {
        return [
          { key: 'productCode', required: true },
          { key: 'imei', required: true },
          { key: 'unitCost', required: true },
          { key: 'note', required: false },
        ];
      }
      return [
        { key: 'productCode', required: true },
        { key: 'unitCost', required: true },
        { key: 'note', required: false },
      ];
    },
    excelImportApiNote() {
      const endpoint = this.isImeiFlow
        ? 'POST v1/opening-balance-cost/validate-import-imei'
        : 'POST v1/opening-balance-cost/validate-import-product';
      return `Hệ thống sẽ gọi <code>${endpoint}</code> kèm <code>companyId</code> — không dùng cột <code>imei id</code>.`;
    },
    canApplyExcelImport() {
      return this.excelStaging.length > 0;
    },
    linesForSubmit() {
      return this.isImeiFlow ? [...this.linesImei] : [...this.linesProduct];
    },
    lineTableItemsProduct() {
      const start = this.linesSliceStart;
      return this.linesProduct.slice(start, start + this.linesPageSizeClamped).map((line, idx) => ({
        ...line,
        count: start + idx + 1,
        _idx: start + idx,
      }));
    },
    lineTableItemsImei() {
      const start = this.linesSliceStart;
      return this.linesImei.slice(start, start + this.linesPageSizeClamped).map((line, idx) => ({
        ...line,
        count: start + idx + 1,
        _idx: start + idx,
      }));
    },
    canSubmitFoot() {
      return (
        !this.busy &&
        this.canEdit &&
        this.scopeOk &&
        this.linesForSubmit.length > 0 &&
        this.validateLinesSilently()
      );
    },
    submitFailedLineCount() {
      return Object.keys(this.submitLineErrors || {}).length;
    },
    submitErrorBannerShow() {
      return !!(this.submitLastGlobalError || this.submitFailedLineCount > 0);
    },
    submitErrorBannerVariant() {
      return this.submitFailedLineCount > 0 ? 'warning' : 'danger';
    },
    draftStorageKey() {
      return `obc-local-draft-${this.$route.name}-${Number(this.form.companyId) || 'na'}`;
    },
    linesCount() {
      return this.isImeiFlow ? this.linesImei.length : this.linesProduct.length;
    },
    linesDetailSubtitle() {
      const mode = this.isImeiFlow ? 'IMEI' : 'SP thường';
      return `${this.linesCount} dòng · ${mode}`;
    },
    totalOriginalPriceFormatted() {
      const arr = this.isImeiFlow ? this.linesImei : this.linesProduct;
      let sum = 0;
      arr.forEach((row) => {
        const p = Number(row.originalPrice);
        if (Number.isFinite(p)) sum += p;
      });
      return `${new Intl.NumberFormat('vn-VN').format(sum)} đ`;
    },
    stickySummaryText() {
      return `${this.linesCount} dòng · Tổng giá vốn ${this.totalOriginalPriceFormatted}`;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        this.setObcBreadcrumb();
      },
    },
    linesCount() {
      this.clampLinesPage();
    },
  },
  mounted() {
    this.syncCompanyFromStorage();
    this.bootstrapStores();
  },
  methods: {
    setObcBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kho hàng', route: '/inventory-receipt' },
        { title: 'Giá vốn theo kỳ', route: '/inventory-cost-recalculation' },
        { title: 'Nhập giá vốn đầu kỳ' },
      ]);
    },
    goCancel() {
      this.$router.push({ name: 'list-opening-balance-cost' });
    },
    switchToProductFlow() {
      if (!this.isImeiFlow) return;
      this.stockMeta.sourceWarehouseId = null;
      this.stockMeta.sourceWarehouseName = '';
      this.form.storeId = null;
      this.$router.push({ name: 'add-opening-balance-cost' });
    },
    switchToImeiFlow() {
      if (this.isImeiFlow) return;
      this.$router.push({ name: 'add-opening-imei-balance-cost' });
    },
    saveLocalDraft() {
      try {
        const payload = {
          v: 1,
          routeName: this.$route.name,
          stockMeta: { ...this.stockMeta },
          linesProduct: this.linesProduct,
          linesImei: this.linesImei,
          storeId: this.form.storeId,
        };
        sessionStorage.setItem(this.draftStorageKey, JSON.stringify(payload));
        makeToastSuccess('Đã lưu nháp cục bộ trên trình duyệt.');
      } catch (_) {
        makeToastFaile('Không lưu được nháp.');
      }
    },
    restoreLocalDraft() {
      try {
        const raw = sessionStorage.getItem(this.draftStorageKey);
        if (!raw) {
          makeToastFaile('Chưa có nháp cục bộ cho phiên này.');
          return;
        }
        const payload = JSON.parse(raw);
        if (!payload || payload.routeName !== this.$route.name) {
          makeToastFaile('Nháp không khớp luồng hiện tại.');
          return;
        }
        if (payload.stockMeta && typeof payload.stockMeta === 'object') {
          Object.assign(this.stockMeta, payload.stockMeta);
        }
        if (Number(payload.storeId) > 0) {
          this.form.storeId = Number(payload.storeId);
          this.stockMeta.sourceWarehouseId = Number(payload.storeId);
          const opt = this.storeOptions.find((s) => s.id === Number(payload.storeId));
          if (opt) this.stockMeta.sourceWarehouseName = opt.label;
        }
        if (Array.isArray(payload.linesProduct)) this.linesProduct = payload.linesProduct;
        if (Array.isArray(payload.linesImei)) this.linesImei = payload.linesImei;
        makeToastSuccess('Đã khôi phục nháp cục bộ.');
      } catch (_) {
        makeToastFaile('Không đọc được nháp.');
      }
    },
    clearLocalDraft() {
      try {
        sessionStorage.removeItem(this.draftStorageKey);
        makeToastSuccess('Đã xóa nháp cục bộ.');
      } catch (_) {
        makeToastFaile('Không xóa được nháp.');
      }
    },
    clearLocalDraftSilently() {
      try {
        sessionStorage.removeItem(this.draftStorageKey);
      } catch (_) {}
    },
    downloadExcelTemplate() {
      const wb = xlsx.utils.book_new();
      if (!this.isImeiFlow) {
        const ws = xlsx.utils.aoa_to_sheet([
          ['Mã hàng', 'Giá vốn', 'Tên hàng'],
          ['3000000000006', 250000, 'Ví dụ — thay bằng dữ liệu thật'],
        ]);
        xlsx.utils.book_append_sheet(wb, ws, 'Sheet1');
        xlsx.writeFile(wb, 'mau-nhap-gia-von-dau-ky-sp.xlsx');
        return;
      }
      /** IMEI: cột file như NK IMEI; validate qua opening-balance-cost/validate-import-imei. */
      const ws = xlsx.utils.aoa_to_sheet([
        ['Mã hàng', 'IMEI', 'Giá vốn', 'Tên hàng', 'Ngày nhập', 'Nhà cung cấp', 'Giá bán IMEI'],
        ['3000000000006', 'CB0000000001', 250000, 'Ví dụ theo IMEI serial', '16/05/2026', '', 0],
      ]);
      xlsx.utils.book_append_sheet(wb, ws, 'Sheet1');
      xlsx.writeFile(wb, 'mau-nhap-gia-von-dau-ky-imei.xlsx');
    },
    lineProductForInfo(idx, isImeiBranch) {
      const arr = isImeiBranch ? this.linesImei : this.linesProduct;
      const row = arr[idx];
      if (!row) {
        return {
          productId: '',
          productCode: '',
          productName: '',
          productImageUrl: '',
          barCode: '',
        };
      }
      return {
        productId: row.productId || '',
        productCode: row.productCode || '',
        productName: row.productName || '',
        productImageUrl: row.productImageUrl || '',
        barCode: row.barCode || row.productCode || '',
      };
    },
    lineResolveOkBadge(item) {
      const arr = this.isImeiFlow ? this.linesImei : this.linesProduct;
      const row = arr[item._idx];
      if (!row || !this.isNonEmptyProductId(row.productId)) return '';
      if (this.isImeiFlow) {
        if (Number.isFinite(Number(row.imeiIdNum)) && Number(row.imeiIdNum) > 0) return 'Đã validate IMEI';
        return '';
      }
      return 'Đã gán productId';
    },
    convertPrice,
    genLineKey() {
      return `L${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    },
    makeRow(kind) {
      return {
        lineKey: this.genLineKey(),
        kind,
        productId: '',
        productName: '',
        productCode: '',
        barCode: '',
        productImageUrl: '',
        productType: null,
        imeiNo: '',
        imeiIdNum: null,
        originalPrice: null,
      };
    },
    removeRow(i) {
      if (this.isImeiFlow) this.linesImei.splice(i, 1);
      else this.linesProduct.splice(i, 1);
      this.clampLinesPage();
    },
    addEmptyLine() {
      if (!this.canEdit || !this.scopeOk) return;
      const row = this.makeRow(this.isImeiFlow ? K_IMEI : K_PRODUCT);
      if (this.isImeiFlow) this.linesImei.push(row);
      else this.linesProduct.push(row);
      this.linesPagination.page = this.linesTotalPages;
      this.$nextTick(() => this.scrollToLinesTableSection());
    },
    clampLinesPage() {
      const max = this.linesTotalPages;
      if (this.linesPagination.page > max) this.linesPagination.page = max;
      if (this.linesPagination.page < 1) this.linesPagination.page = 1;
    },
    resetLinesPage() {
      this.linesPagination.page = 1;
    },
    onLinesPageSizeChange() {
      this.resetLinesPage();
    },
    setLineOriginalPrice(idx, value) {
      const arr = this.isImeiFlow ? this.linesImei : this.linesProduct;
      const row = arr[idx];
      if (!row) return;
      row.originalPrice = value;
    },
    formatLineOriginalPrice(row) {
      if (!row || row.originalPrice == null || row.originalPrice === '') return '—';
      return convertPrice(Number(row.originalPrice), 'vn-VN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
    },
    formatStoresUpdatedCell(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) return '—';
      return `${new Intl.NumberFormat('vn-VN').format(n)} kho`;
    },
    onSelectedSourceWarehouse(option) {
      const it = option && option.item;
      if (!it) return;
      this.stockMeta.sourceWarehouseName = it.name || '';
      this.stockMeta.sourceWarehouseId = Number(it.id);
      this.form.storeId = Number(it.id);
    },
    onInputChangeSourceWarehouse(text) {
      this.stockMeta.sourceWarehouseName = text;
    },
    extractEnvelopeMessage(envelope, fallback = 'Thao tác thất bại') {
      if (envelope == null || typeof envelope !== 'object') return fallback;
      const direct =
        envelope.message ||
        envelope.Message ||
        envelope.error ||
        envelope.detail ||
        envelope.title;
      if (typeof direct === 'string' && direct.trim()) return direct.trim();
      const errs = envelope.errors;
      if (errs && typeof errs === 'object') {
        if (Array.isArray(errs)) {
          const firstStr = errs.find((x) => typeof x === 'string' && x.trim());
          if (firstStr) return String(firstStr).trim();
          const firstMsg = errs.find((x) => x && typeof x.message === 'string');
          if (firstMsg && firstMsg.message.trim()) return firstMsg.message.trim();
        } else {
          const keys = Object.keys(errs);
          for (let i = 0; i < keys.length; i++) {
            const v = errs[keys[i]];
            if (Array.isArray(v) && v.length) {
              const el = v[0];
              if (typeof el === 'string' && el.trim()) return el.trim();
            } else if (typeof v === 'string' && v.trim()) return v.trim();
          }
        }
      }
      return fallback;
    },
    isHttpEnvelopeOk(envelope, allow = [200]) {
      const s = Number(envelope && envelope.status);
      return allow.includes(s);
    },
    batchResultRowIsFailure(row) {
      if (!row || typeof row !== 'object') return false;
      if (row.success === false || row.failed === true) return true;
      const s = String(row.status != null ? row.status : '').trim().toLowerCase();
      const okLike = s === 'ok' || s === 'success' || s === 'applied';
      if (okLike) return false;
      const msg = `${row.errorMessage || row.message || row.Message || row.error || ''}`.trim();
      if (msg) return true;
      if (s === 'fail' || s === 'failed' || s === 'error') return true;
      return false;
    },
    extractBatchRowErrorMessage(row) {
      if (!row || typeof row !== 'object') return 'Dòng không lưu được';
      const m = `${row.errorMessage || row.message || row.Message || row.error || ''}`.trim();
      if (m) return m;
      const st = row.status != null ? String(row.status).trim() : '';
      return st ? `Trạng thái: ${st}` : 'Dòng không lưu được';
    },
    ingestBatchResultsToLineErrors(results) {
      const list = Array.isArray(results) ? results : [];
      list.forEach((r) => {
        const lk =
          r && (r.lineKey != null ? String(r.lineKey) : r.line_key != null ? String(r.line_key) : '');
        if (!lk) return;
        if (this.batchResultRowIsFailure(r)) {
          this.$set(this.submitLineErrors, lk, this.extractBatchRowErrorMessage(r));
        }
      });
    },
    tbodyTrClassSubmitErrors(item, type) {
      if (type !== 'row' || !item || !item.lineKey) return null;
      return this.submitLineErrors[item.lineKey] ? 'obc-batch-line-has-error table-warning' : null;
    },
    batchResultStatusBadgeVariant(status) {
      const s = String(status != null ? status : '').toLowerCase();
      if (s === 'ok' || s === 'success' || s === 'applied') return 'success';
      return 'danger';
    },
    scrollSubmitErrorBannerIntoView() {
      const ref = this.$refs.obcSubmitErrBanner;
      const el = ref && ref.$el ? ref.$el : ref;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },
    scrollToBatchDetailSection() {
      const ref = this.$refs.obcBatchResultCard;
      const el = ref && ref.$el ? ref.$el : ref;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    scrollToLinesTableSection() {
      const el =
        typeof document !== 'undefined' ? document.getElementById('obc-lines-table-section') : null;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    syncCompanyFromStorage() {
      const cid = parseInt(getData('currentCompanyId'), 10);
      this.form.companyId = Number.isFinite(cid) && cid > 0 ? cid : null;
    },
    bootstrapStores() {
      ApiService.get('/stores/getStores')
        .then((response) => {
          const list = response.data.data || [];
          this.allStoresFlat = list.map((s) => ({
            id: Number(s.id),
            companyId: Number(s.companyId),
            label:
              `${s.shortName || ''}${s.shortName ? ' · ' : ''}${s.name || ''}`.trim() || `#${s.id}`,
          }));
        })
        .catch(() => {});
    },
    isNonEmptyProductId(pid) {
      return String(pid == null ? '' : pid).trim().length > 0;
    },
    validateLinesSilently() {
      if (this.isImeiFlow) {
        for (let i = 0; i < this.linesImei.length; i++) {
          const row = this.linesImei[i];
          if (!this.isNonEmptyProductId(row.productId)) return false;
          const p = Number(row.originalPrice);
          if (!Number.isFinite(p) || p < 0) return false;
          const im = Number(row.imeiIdNum);
          if (!Number.isFinite(im) || im <= 0) return false;
        }
        return true;
      }
      for (let i = 0; i < this.linesProduct.length; i++) {
        const row = this.linesProduct[i];
        if (!this.isNonEmptyProductId(row.productId)) return false;
        const p = Number(row.originalPrice);
        if (!Number.isFinite(p) || p < 0) return false;
      }
      return true;
    },
    validateLinesWithToast() {
      if (!this.hasHeaderCompanyId) {
        makeToastFaile('Chưa có doanh nghiệp — chọn trên header (localStorage.currentCompanyId).');
        return false;
      }
      if (this.isImeiFlow && !(Number(this.form.storeId) > 0)) {
        makeToastFaile('Chọn cửa hàng trước khi nhập Excel IMEI.');
        return false;
      }
      if (!this.stockMeta.stockSlipDate || !String(this.stockMeta.stockSlipDate).trim()) {
        makeToastFaile('Chọn ngày tham chiếu.');
        return false;
      }
      if (this.isImeiFlow) {
        if (!this.linesImei.length) {
          makeToastFaile('Chưa có dòng IMEI — nhập Excel (Mã hàng + IMEI serial + Giá vốn).');
          return false;
        }
        for (let i = 0; i < this.linesImei.length; i++) {
          const row = this.linesImei[i];
          if (!this.isNonEmptyProductId(row.productId)) {
            makeToastFaile(`IMEI dòng ${i + 1}: chưa có productId sau validate opening-balance-cost — kiểm tra Mã hàng / IMEI.`);
            return false;
          }
          const p = Number(row.originalPrice);
          if (!Number.isFinite(p) || p < 0) {
            makeToastFaile(`IMEI dòng ${i + 1}: giá không hợp lệ.`);
            return false;
          }
          if (!Number.isFinite(Number(row.imeiIdNum)) || Number(row.imeiIdNum) <= 0) {
            makeToastFaile(
              `IMEI dòng ${i + 1}: thiếu imeiId sau validate — kiểm tra API opening-balance-cost/validate-import-imei.`,
            );
            return false;
          }
        }
        return true;
      }
      if (!this.linesProduct.length) {
        makeToastFaile('Chưa có dòng SP — nhập Excel (màn Nhập giá SP).');
        return false;
      }
      for (let i = 0; i < this.linesProduct.length; i++) {
        const row = this.linesProduct[i];
        if (!this.isNonEmptyProductId(row.productId)) {
          makeToastFaile(`SP thường dòng ${i + 1}: chưa có productId — kiểm tra validate Mã hàng.`);
          return false;
        }
        const p = Number(row.originalPrice);
        if (!Number.isFinite(p) || p < 0) {
          makeToastFaile(`SP thường dòng ${i + 1}: giá không hợp lệ.`);
          return false;
        }
      }
      return true;
    },
    buildPayloadLine(row) {
      const base = {
        lineKey: row.lineKey,
        productId: row.productId,
        originalPrice: Number(row.originalPrice),
      };
      if (row.kind === K_IMEI) {
        base.imeiId = Number(row.imeiIdNum);
        const serial = String(row.imeiNo || '').trim();
        if (serial) base.imeiNo = serial;
      }
      return base;
    },
    chunkArray(arr, size) {
      const out = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    },
    mergeBatchEnvelope(envelope) {
      if (!envelope || typeof envelope !== 'object') return;
      const d = envelope.data && typeof envelope.data === 'object' ? envelope.data : {};
      const results = Array.isArray(d.results) ? d.results : [];
      this.resultBatches.push({
        processedCount: Number(d.processedCount) || 0,
        failedCount: Number(d.failedCount) || 0,
        results,
      });
      this.ingestBatchResultsToLineErrors(results);
    },
    async submitBatches() {
      if (!this.validateLinesWithToast()) return;
      this.busy = true;
      this.resultBatches = [];
      this.submitLineErrors = {};
      this.submitLastGlobalError = '';
      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID ? `fe-obc-${crypto.randomUUID()}` : undefined;
      const scheduleErrorScroll = () => {
        this.$nextTick(() => this.scrollSubmitErrorBannerIntoView());
      };
      try {
        const chunks = this.chunkArray(this.linesForSubmit.map((r) => this.buildPayloadLine(r)), this.chunkSize);
        const memoTrim = String(this.memoForApi || '').trim();
        for (let c = 0; c < chunks.length; c++) {
          const body = {
            companyId: Number(this.form.companyId),
            lines: chunks[c],
          };
          if (this.isImeiFlow && Number(this.form.storeId) > 0) {
            body.storeId = Number(this.form.storeId);
          }
          if (memoTrim) body.memo = memoTrim;
          if (idempotencyKey) body.idempotencyKey = `${idempotencyKey}-c${c}`;
          const postBatch = this.isImeiFlow
            ? postOpeningBalanceCostBatchImeiStock
            : postOpeningBalanceCostBatchProductStock;
          try {
            const axiosRes = await postBatch(body);
            const httpSt = Number(axiosRes.status);
            const envelope = axiosRes.data || {};
            if (httpSt === 422) {
              this.mergeBatchEnvelope(envelope);
              continue;
            }
            if (!this.isHttpEnvelopeOk(envelope, [200, 201])) {
              const dBad = envelope.data && typeof envelope.data === 'object' ? envelope.data : {};
              this.ingestBatchResultsToLineErrors(dBad.results);
              this.submitLastGlobalError = this.extractEnvelopeMessage(envelope);
              makeToastFaile(this.submitLastGlobalError);
              scheduleErrorScroll();
              return;
            }
            this.mergeBatchEnvelope(envelope);
          } catch (e) {
            const st = e && e.response && Number(e.response.status);
            const ed = e && e.response && e.response.data;
            if (st === 422 && ed && typeof ed === 'object') {
              this.mergeBatchEnvelope(ed);
              continue;
            }
            if (ed && typeof ed === 'object') {
              const inner = ed.data && typeof ed.data === 'object' ? ed.data : ed;
              this.ingestBatchResultsToLineErrors(inner.results);
            }
            const msg = this.extractEnvelopeMessage(ed, (e && e.message) || 'Lỗi mạng');
            this.submitLastGlobalError = msg;
            makeToastFaile(msg);
            scheduleErrorScroll();
            return;
          }
        }
        const totalFailed = this.resultBatches.reduce((s, b) => s + (Number(b.failedCount) || 0), 0);
        if (totalFailed > 0) {
          getToastInstance().makeToastWarning(
            `Đã nhận phản hồi nhưng còn ${totalFailed} dòng lỗi — xem banner và cột «Phản hồi lưu», hoặc bảng kết quả chi tiết bên dưới.`,
          );
          scheduleErrorScroll();
          return;
        }
        makeToastSuccess(`Đã lưu — ${chunks.length} batch đã gửi.`);
        this.clearLocalDraftSilently();
        this.$router.push({ name: 'list-opening-balance-cost', query: { ok: String(Date.now()) } });
      } catch (e) {
        const ed = e && e.response && e.response.data;
        if (ed && typeof ed === 'object') {
          const inner = ed.data && typeof ed.data === 'object' ? ed.data : ed;
          this.ingestBatchResultsToLineErrors(inner.results);
        }
        const msg = this.extractEnvelopeMessage(ed, e.message || 'Lỗi mạng');
        this.submitLastGlobalError = msg;
        makeToastFaile(msg);
        scheduleErrorScroll();
      } finally {
        this.busy = false;
      }
    },
    showModalImport() {
      this.resetExcelImportState();
      this.$refs['import-modal'].show();
    },
    closeModalImport() {
      this.$refs['import-modal'].hide();
    },
    resetExcelImportState() {
      this.excelStaging = [];
      this.excelImportFileName = '';
      this.excelImportDragOver = false;
      const input = this.$refs.excelFileInput;
      if (input) input.value = '';
    },
    triggerExcelFilePick() {
      const input = this.$refs.excelFileInput;
      if (input) input.click();
    },
    onExcelDragOver() {
      this.excelImportDragOver = true;
    },
    onExcelDragLeave() {
      this.excelImportDragOver = false;
    },
    onExcelDrop(e) {
      this.excelImportDragOver = false;
      const file = e && e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.processExcelFile(file);
    },
    onExcelFileSelected(e) {
      const file = e && e.target && e.target.files && e.target.files[0];
      if (file) this.processExcelFile(file);
    },
    clearExcelFile() {
      this.excelStaging = [];
      this.excelImportFileName = '';
      const input = this.$refs.excelFileInput;
      if (input) input.value = '';
    },
    processExcelFile(file) {
      if (!file) return;
      const name = file.name || '';
      if (!/\.(xls|xlsx|csv)$/i.test(name)) {
        makeToastFaile('Chọn file .xlsx, .xls hoặc .csv');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        makeToastFaile('File tối đa 10MB');
        return;
      }
      this.excelImportFileName = name;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb = xlsx.read(ev.target.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = xlsx.utils.sheet_to_json(ws, { blankRows: false });
          this.excelStaging = this.normalizeExcelRows(rows);
          if (!rows.length) {
            makeToastFaile('File không có dòng dữ liệu.');
            this.excelStaging = [];
            return;
          }
          makeToastSuccess(`Đọc được ${rows.length} dòng.`);
        } catch (_) {
          makeToastFaile('Không đọc được excel.');
          this.excelStaging = [];
          this.excelImportFileName = '';
        }
      };
      reader.readAsBinaryString(file);
    },
    normalizeExcelRows(rows) {
      if (!Array.isArray(rows)) return [];
      return rows.map((row) => {
        const normalized = {};
        Object.keys(row || {}).forEach((key) => {
          normalized[String(key).trim()] = row[key];
        });
        return normalized;
      });
    },
    pickExcelColumnValue(row, preferredKeys, fuzzyPattern) {
      if (!row || typeof row !== 'object') return undefined;
      for (let i = 0; i < preferredKeys.length; i++) {
        const key = preferredKeys[i];
        if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
      }
      const keys = Object.keys(row);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (fuzzyPattern && fuzzyPattern.test(String(k).trim())) return row[k];
      }
      return undefined;
    },
    getExcelCostPrice(raw) {
      return this.pickExcelColumnValue(
        raw,
        ['Giá vốn', 'Gia von', 'Giá Vốn', 'Don gia', 'Đơn giá'],
        /gi[aá].*v[oô]n|don\s*gia|đơn\s*giá/i,
      );
    },
    getExcelProductCode(raw) {
      const v = this.pickExcelColumnValue(
        raw,
        ['Mã hàng', 'Ma hang', 'Mã Hàng', 'productCode'],
        /m[aã].*h[aà]ng|product\s*code/i,
      );
      return v == null ? '' : v;
    },
    getExcelImeiSerial(raw) {
      const v = this.pickExcelColumnValue(
        raw,
        ['IMEI', 'Imei', 'ma IMEI', 'Mã IMEI'],
        /^imei$|m[aã]\s*imei/i,
      );
      return this.normalizeImeiSerial(v);
    },
    normalizeImeiSerial(val) {
      if (val == null || val === '') return '';
      if (typeof val === 'number' && Number.isFinite(val)) {
        return Number.isSafeInteger(val) ? String(val) : String(val).trim();
      }
      return String(val).trim();
    },
    buildImeiImportPriceKey(imeiCode) {
      const serial = this.normalizeImeiSerial(imeiCode);
      return serial ? serial.toUpperCase() : '';
    },
    buildProductImportMatchKey(productCode) {
      return String(productCode || '')
        .trim()
        .toUpperCase();
    },
    getExcelProductName(raw) {
      const v = this.pickExcelColumnValue(
        raw,
        ['Tên hàng', 'Ten hang', 'Tên Hàng', 'productName'],
        /t[eê]n.*h[aà]ng|product\s*name/i,
      );
      return v == null ? '' : v;
    },
    getExcelSellingPrice(raw) {
      return this.pickExcelColumnValue(
        raw,
        ['Giá bán IMEI', 'Gia ban IMEI'],
        /gi[aá].*b[aá]n.*imei/i,
      );
    },
    parseExcelPriceCell(rawVal) {
      if (rawVal == null || (typeof rawVal === 'string' && rawVal.trim() === '')) {
        return 0;
      }
      const n = parseDecimalNumber(rawVal);
      return Number.isFinite(n) ? n : NaN;
    },
    resolveImportUnitPrice(excelPrice, item) {
      if (excelPrice != null) {
        const fromExcel = parseDecimalNumber(excelPrice);
        if (Number.isFinite(fromExcel) && fromExcel >= 0) return fromExcel;
      }
      const candidates = [
        item && item.unitPrice,
        item && Number(item.originalPrice) > 0 ? item.originalPrice : null,
        item && item.beforeVatPrice,
        item && item.afterVatPrice,
      ];
      for (let i = 0; i < candidates.length; i++) {
        const n = parseDecimalNumber(candidates[i]);
        if (Number.isFinite(n) && n >= 0) return n;
      }
      return null;
    },
    lookupExcelPriceByImeiItem(excelPriceByKey, item) {
      if (!excelPriceByKey || !item) return undefined;
      const key = this.buildImeiImportPriceKey(item.imeiNo || item.imeiCode || item.productImei);
      if (!key) return undefined;
      if (excelPriceByKey.has(key)) return excelPriceByKey.get(key);
      return undefined;
    },
    lookupExcelPriceByProductItem(excelPriceByKey, item) {
      if (!excelPriceByKey || !item) return undefined;
      const key = this.buildProductImportMatchKey(item.productCode);
      if (excelPriceByKey.has(key)) return excelPriceByKey.get(key);
      return undefined;
    },
    unwrapOpeningStockSlipValidate(axiosRes) {
      const inner = axiosRes && axiosRes.data && axiosRes.data.data;
      if (!inner || typeof inner !== 'object') {
        const msg =
          axiosRes &&
          axiosRes.data &&
          (axiosRes.data.message || axiosRes.data.Message || '');
        return { ok: false, message: String(msg || '').trim(), result: null };
      }
      const ok = Number(inner.status) === 1;
      return {
        ok,
        message: String(inner.message || '').trim(),
        result: inner.data,
      };
    },
    pickImeiPkFromValidatedItem(item) {
      const candidates = [
        item && item.imeiId,
        item && item.productImeiStockId,
        item && item.productImeiId,
        item && item.stockImeiId,
        item && item.productImeiStockID,
      ];
      for (let i = 0; i < candidates.length; i++) {
        const n = Number(candidates[i]);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    },
    appendOpeningBalanceProductRow(item, productCodeHint, excelPrice) {
      const row = this.makeRow(K_PRODUCT);
      row.productId = String((item && item.productId) || '').trim();
      row.productCode = String((item && item.productCode) || productCodeHint || '').trim();
      row.productName = String((item && item.productName) || (item && item.name) || '').trim();
      row.barCode = (item && (item.barCode || item.barcode)) || '';
      const u = this.resolveImportUnitPrice(excelPrice, item);
      row.originalPrice = u;
      if (this.isNonEmptyProductId(row.productId) && Number.isFinite(Number(row.originalPrice)) && Number(row.originalPrice) >= 0) {
        this.linesProduct.push(row);
        return true;
      }
      return false;
    },
    appendOpeningBalanceImeiRow(itemMeta, imeiPk, excelPrice) {
      const item = itemMeta || {};
      const row = this.makeRow(K_IMEI);
      row.productId = String(item.productId || '').trim();
      row.productCode = String(item.productCode || item.code || '').trim();
      row.productName = String(item.productName || item.name || '').trim();
      row.barCode = (item && (item.barCode || item.barcode)) || '';
      row.productImageUrl = String((item && (item.imageUrl || item.productImageUrl)) || '').trim();
      row.imeiNo = String(item.imeiNo || item.imeiCode || item.productImei || '').trim();
      row.imeiIdNum =
        imeiPk != null && Number.isFinite(Number(imeiPk)) && Number(imeiPk) > 0
          ? Number(imeiPk)
          : this.pickImeiPkFromValidatedItem(item);
      const u = this.resolveImportUnitPrice(excelPrice, item);
      row.originalPrice = u;
      if (
        !this.isNonEmptyProductId(row.productId) ||
        !Number.isFinite(Number(row.originalPrice)) ||
        Number(row.originalPrice) < 0 ||
        !Number.isFinite(Number(row.imeiIdNum)) ||
        Number(row.imeiIdNum) <= 0
      ) {
        return false;
      }
      this.linesImei.push(row);
      return true;
    },
    async applyExcelImport() {
      if (!this.excelStaging.length) {
        makeToastFaile('Không có dữ liệu excel.');
        return;
      }
      if (!this.scopeOk) {
        makeToastFaile(
          this.isImeiFlow
            ? 'Chọn cửa hàng và doanh nghiệp (header) trước khi nhập Excel.'
            : 'Chọn doanh nghiệp trên header trước khi nhập Excel.',
        );
        return;
      }
      this.busyImport = true;
      try {
        if (!this.isImeiFlow) {
          const listMeta = [];
          const listProduct = [];
          const excelPriceByProductCode = new Map();
          for (const raw of this.excelStaging) {
            const productCode = String(this.getExcelProductCode(raw)).trim();
            if (!productCode) continue;
            const priceNum = this.parseExcelPriceCell(this.getExcelCostPrice(raw));
            if (!Number.isFinite(priceNum) || priceNum < 0) continue;
            const matchKey = this.buildProductImportMatchKey(productCode);
            listMeta.push({
              productCode,
              excelPrice: priceNum,
            });
            excelPriceByProductCode.set(matchKey, priceNum);
            listProduct.push({
              productCode,
              productName: raw['Tên hàng'] ? String(raw['Tên hàng']) : '',
              quantity: 1,
              unitPrice: priceNum,
            });
          }
          if (!listProduct.length) {
            makeToastFaile('Không có dòng hợp lệ (cần Mã hàng + Giá vốn).');
            return;
          }
          const axiosRes = await postOpeningStockSlipValidateImportProduct({
            listProduct,
          });
          const { ok, message, result } = this.unwrapOpeningStockSlipValidate(axiosRes);
          if (!ok || !Array.isArray(result)) {
            makeToastFaile(message || 'Validate Mã hàng thất bại (NK đầu kỳ — product).');
            if (Array.isArray(result)) {
              result.filter((x) => typeof x === 'string').slice(0, 8).forEach((m) => makeToastFaile(m));
            }
            return;
          }
          let added = 0;
          for (let i = 0; i < result.length; i++) {
            const item = result[i];
            const hint = listMeta[i] ? listMeta[i].productCode : '';
            const exP =
              this.lookupExcelPriceByProductItem(excelPriceByProductCode, item) ||
              (listMeta[i] ? listMeta[i].excelPrice : undefined);
            if (this.appendOpeningBalanceProductRow(item, hint, exP)) added += 1;
          }
          if (!added) {
            makeToastFaile('Không ghép được dòng SP sau validate — kiểm tra Mã hàng / phản hồi API.');
          } else {
            makeToastSuccess(`Đã thêm ${added} dòng SP (đã có productId).`);
            this.resetLinesPage();
            this.closeModalImport();
          }
          return;
        }

        /** IMEI: Mã hàng + IMEI serial + Giá vốn → POST opening-balance-cost/validate-import-imei (+ companyId). */
        const nkPayloads = [];
        const excelPriceByImeiKey = new Map();
        const MAX_VALIDATE_IMPORT_IMEI = 500;

        for (const raw of this.excelStaging) {
          const productCode = String(this.getExcelProductCode(raw)).trim();
          const priceNum = this.parseExcelPriceCell(this.getExcelCostPrice(raw));
          if (!productCode || !Number.isFinite(priceNum) || priceNum < 0) continue;

          const imeiSerialRaw = this.getExcelImeiSerial(raw);

          if (!imeiSerialRaw) continue;

          excelPriceByImeiKey.set(this.buildImeiImportPriceKey(imeiSerialRaw), priceNum);
          nkPayloads.push({
            productCode,
            imeiCode: imeiSerialRaw,
            productName: String(this.getExcelProductName(raw) || '').trim(),
            unitPrice: priceNum,
            sellingPrice: this.parseExcelPriceCell(this.getExcelSellingPrice(raw)) || 0,
          });
        }

        if (!nkPayloads.length) {
          makeToastFaile(
            'Không có dòng hợp lệ — mỗi dòng cần Mã hàng + IMEI (serial) + Giá vốn.',
          );
          return;
        }

        const companyId = Number(this.form.companyId);
        const mergedResults = [];

        for (let offset = 0; offset < nkPayloads.length; offset += MAX_VALIDATE_IMPORT_IMEI) {
          const chunk = nkPayloads.slice(offset, offset + MAX_VALIDATE_IMPORT_IMEI);
          const axiosResImei = await postOpeningBalanceCostValidateImportImei({
            companyId,
            importProducts: chunk,
          });
          const imeiUnpack = this.unwrapOpeningStockSlipValidate(axiosResImei);
          if (!imeiUnpack.ok || !Array.isArray(imeiUnpack.result)) {
            makeToastFaile(
              imeiUnpack.message || 'Validate IMEI thất bại (opening-balance-cost/validate-import-imei).',
            );
            if (Array.isArray(imeiUnpack.result)) {
              imeiUnpack.result
                .filter((x) => typeof x === 'string')
                .slice(0, 8)
                .forEach((m) => makeToastFaile(m));
            }
            return;
          }
          mergedResults.push(...imeiUnpack.result);
        }

        if (mergedResults.length !== nkPayloads.length) {
          makeToastFaile(
            `Số dòng phản hồi validate (${mergedResults.length}) không khớp file (${nkPayloads.length}).`,
          );
          return;
        }

        let added = 0;
        let nkSkip = 0;
        for (let i = 0; i < mergedResults.length; i++) {
          const item = mergedResults[i];
          const exP = this.lookupExcelPriceByImeiItem(excelPriceByImeiKey, item);
          if (this.appendOpeningBalanceImeiRow(item, null, exP)) added += 1;
          else nkSkip += 1;
        }

        if (!added) {
          makeToastFaile(
            nkSkip
              ? 'Validate IMEI trả về nhưng không ghép được dòng — kiểm tra API có trả productId và imeiId.'
              : 'Không ghép được dòng IMEI — kiểm tra file và phản hồi validate.',
          );
        } else {
          makeToastSuccess(`Đã thêm ${added} dòng IMEI.`);
          this.resetLinesPage();
          this.closeModalImport();
        }
      } catch (e) {
        makeToastFaile(
          this.extractEnvelopeMessage(e && e.response && e.response.data, e.message || 'Lỗi mạng khi validate.'),
        );
      } finally {
        this.busyImport = false;
      }
    },
  },
};
</script>

<style lang="scss">
.opening-balance-cost-page.stock-slips {
  padding-bottom: 5.5rem;

  .obc-page-heading .card-label {
    font-size: 1.35rem;
  }

  .obc-page-subtitle {
    max-width: 42rem;
    line-height: 1.45;
  }

  .obc-badge-draft {
    font-weight: 600;
  }

  .obc-toolbar-actions > .obc-toolbar-btn,
  .obc-toolbar-actions > .dropdown {
    margin-left: 0.35rem;
  }

  .obc-toolbar-actions > .obc-toolbar-btn:first-child,
  .obc-toolbar-actions > .dropdown:first-child {
    margin-left: 0;
  }

  .obc-btn-apply {
    min-width: 8.25rem;
  }

  .obc-side-card {
    border-radius: 0.35rem;
    background-color: #ffffff;
  }

  .obc-segment-track {
    background-color: #f3f6f9 !important;
    border-color: #ebedf3 !important;
  }

  .obc-segment-btn {
    border: 0;
    background: transparent;
    padding: 0.45rem 0.65rem;
    border-radius: 0.3rem;
    font-size: 0.8125rem;
    color: #7e8299;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }

  .obc-segment-btn:disabled {
    opacity: 0.55;
  }

  .obc-segment-btn.active {
    background: #ffffff;
    color: #181c32;
    box-shadow: 0 1px 4px rgba(24, 28, 50, 0.08);
  }

  .obc-hints-alert {
    border-radius: 0.35rem;
  }

  .obc-import-modal {
    border: 0;
    border-radius: 0.5rem;
  }

  .obc-import-modal__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.35rem;
    background: #e8fff3;
    color: #1bc5bd;
    font-size: 1.15rem;
  }

  .obc-import-modal__close {
    font-size: 1.5rem;
    line-height: 1;
    opacity: 0.45;
  }

  .obc-import-col-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.65rem;
    border: 1px solid #e4e6ef;
    border-radius: 0.35rem;
    background: #ffffff;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #3f4254;
  }

  .obc-import-col-dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    margin-right: 0.45rem;
    flex-shrink: 0;
  }

  .obc-import-col-dot--required {
    background: #f64e60;
  }

  .obc-import-col-dot--optional {
    background: #b5b5c3;
  }

  .obc-import-template-link {
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .obc-import-dropzone {
    border: 2px dashed #e4e6ef;
    border-radius: 0.42rem;
    padding: 2rem 1.5rem;
    background: #fafbfc;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  .obc-import-dropzone--active {
    border-color: #3699ff;
    background: #eef6ff;
  }

  .obc-import-dropzone__icon {
    font-size: 2.25rem;
  }

  .obc-import-file-info {
    padding: 0.25rem 0;
  }

  .obc-import-api-note {
    color: #7e8299;
    line-height: 1.5;
  }

  .obc-import-api-note code {
    color: #3f4254;
    font-size: 0.8125rem;
  }

  .obc-page-size-select {
    max-width: 8.5rem;
    min-width: 6.5rem;
  }

  .obc-lines-card {
    border-radius: 0.35rem;
    overflow: hidden;
  }

  .obc-detail-toolbar {
    row-gap: 0.35rem;
  }

  .obc-table-head th {
    font-weight: 600;
    background-color: #f3f6f9;
    border-bottom: 1px solid #ebedf3;
  }

  .obc-lines-table.table th,
  .obc-lines-table.table td {
    padding: 0.5rem 0.45rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
  }

  .obc-lines-total {
    border-radius: 0 0 0.35rem 0.35rem;
  }

  .badge-wrap {
    white-space: normal;
    font-weight: 500;
  }

  .obc-product-cell ::v-deep .productCode span {
    font-size: 12px !important;
  }

  tr.obc-batch-line-has-error td {
    box-shadow: inset 4px 0 0 #f0ad4e;
  }

  .obc-submit-error-banner strong {
    font-weight: 600;
  }

  .card-body .table.table-sm td,
  .card-body .table.table-sm th {
    padding: 0.35rem;
  }

  .obc-sticky-actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 101;
  }

  .obc-sticky-inner {
    max-width: 100%;
    margin: 0 auto;
  }

  @media (min-width: 992px) {
    .obc-sticky-inner {
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }
}
</style>
